<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { isNaturalProfile } from '../../../utilities/investment-process/useUserProfileType'
import { usePersonalAccountDataState } from '../../../utilities/account/useAccountDataPersonal'
import { useLegalAccountDataState } from '../../../utilities/account/useAccountDataLegal'
import { ExclamationCircleIcon, CheckCircleIcon, MailIcon, CheckIcon, ChatAlt2Icon } from '@heroicons/vue/solid'
import { DocumentTextIcon, PrinterIcon } from '@heroicons/vue/outline'
import BaseButtonMuted from '../../base/BaseButtonMuted.vue'
import { MembershipDocumentPostStatus, MembershipDocumentStatus } from '../../../typings'
import { requestPhysicalMail } from '../../../utilities/api'
const accountStateNatural = usePersonalAccountDataState()
const accountStateLegal = useLegalAccountDataState()
const account = ref(null)

onMounted(async () => {
    if (isNaturalProfile()) {
        account.value = accountStateNatural.naturalProfile
    } else {
        account.value = accountStateLegal.legalProfile
    }
})

async function handleClickRequestPhysicalMail(membershipDocumentId: string) {
    await requestPhysicalMail(membershipDocumentId)
    isNaturalProfile() ? accountStateNatural.fetchAccountData() : accountStateLegal.fetchAccountData()
}
</script>

<template>
    <h3 class="flex py-4 text-xl font-bold text-gray-900">
        <DocumentTextIcon class="mr-2 w-5" />
        {{ $t('membership_management.document_label') }}
    </h3>
    <div v-if="account">
        <div
            v-if="account.membership_document_description !== null"
            class="mx-5 my-1 mb-4 flex justify-start rounded bg-gray-100 p-2 text-xs text-gray-400">
            <span class="whitespace-break-spaces">{{ account.membership_document_description }}</span>
        </div>
        <div v-for="(doc, index) in account.membership_document" :key="index">
            <div class="flex items-center rounded-lg border border-gray-200 px-2 py-3 sm:px-4">
                <div class="min-w-0 flex-1 items-center md:flex">
                    <div class="flex-shrink-0">
                        <div class="relative flex items-center justify-center rounded-full bg-white p-1">
                            <div class="relative">
                                <DocumentTextIcon class="h-12 w-12 text-gray-300"></DocumentTextIcon>
                                <ExclamationCircleIcon
                                    v-if="doc.status !== MembershipDocumentStatus.ACCEPTED"
                                    class="absolute bottom-[-2px] right-[-2px] h-6 w-6 rounded-full bg-white text-red-400"></ExclamationCircleIcon>
                                <CheckCircleIcon
                                    v-else
                                    class="absolute bottom-[-2px] right-[-2px] h-6 w-6 rounded-full bg-white text-green-500"></CheckCircleIcon>
                            </div>
                        </div>
                    </div>
                    <div class="min-w-0 flex-1 px-4">
                        <div>
                            <p class="mt-1 flex items-center text-sm text-gray-500">
                                Status:
                                {{ $t(`membership_document.status.${doc.status}`) }}
                            </p>
                        </div>
                    </div>
                    <div class="justify-end pt-6 md:pt-0">
                        <div class="flex flex-col space-y-2">
                            <BaseButtonMuted :icon="PrinterIcon" :href="doc.url" target="_blank">
                                {{ $t('membership_management.download_document') }}
                            </BaseButtonMuted>
                        </div>
                        <div class="flex flex-col space-y-2">
                            <BaseButtonMuted
                                v-if="doc.post_status === null"
                                :icon="MailIcon"
                                @click="handleClickRequestPhysicalMail(doc.id)">
                                {{ $t('membership_management.send_document') }}
                            </BaseButtonMuted>
                            <BaseButtonMuted
                                v-else-if="doc.post_status === MembershipDocumentPostStatus.REQUESTED"
                                :disabled="true"
                                :icon="ChatAlt2Icon">
                                {{ $t('membership_management.send_document_requested') }}
                            </BaseButtonMuted>
                            <BaseButtonMuted
                                v-else-if="doc.post_status === MembershipDocumentPostStatus.SENT"
                                :disabled="true"
                                :icon="CheckIcon">
                                {{ $t('membership_management.send_document_sent') }}
                            </BaseButtonMuted>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
